import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectStreamStatus } from '@store/selectors/monitor.selectors';
import { fetchProvidersStatus } from '@apis/auth';
import { setProviderIssue, clearProviderIssue } from '@store/slices/providerStatus.slice';
import { ProjectSelector } from '@components/ProjectSelector/ProjectSelector';
import { TopBarSchedulePickerSlot } from './topBarSchedulePicker';
import { NotificationsBell } from '@components/Notifications/NotificationsBell/NotificationsBell';
import { UserMenu } from '@/Components/UserMenu/UserMenu';
import { ProviderReconnectButton } from './ProviderReconnectButton';
import { useCanAccess } from '@permissions/useFeatures';
import { BriefcaseIcon, ChevronRightIcon } from '@utils/icons';
import styles from './styles.module.css';

export function TopBar({ pageTitle }: { pageTitle: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const streamStatus = useAppSelector(selectStreamStatus);
  const isReconnecting = streamStatus === 'reconnecting';

  // App-level provider status: the TopBar is on every authed page, so this seeds
  // the reconnect prompt globally on load (and re-checks once on window focus) —
  // no interval poll. Steady-state detection is reactive (a provider-backed
  // request that 401s flips the chip via the QueryClient's onError) plus, for
  // Genba, the expiry timer below. Re-fetched after a reconnect via
  // invalidateQueries(['provider-status']) in connectProvider.
  const { data: providers } = useQuery({
    queryKey: ['provider-status'],
    queryFn: ({ signal }) => fetchProvidersStatus(signal),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const genba = providers?.genba;
  const trusstor = providers?.trusstor;
  const genbaExpiresAt = providers?.genbaExpiresAt;
  useEffect(() => {
    for (const [provider, status] of [['genba', genba], ['trusstor', trusstor]] as const) {
      if (!status) continue;
      if (status === 'ok') {
        dispatch(clearProviderIssue(provider));
      } else {
        dispatch(setProviderIssue({ provider, issue: status === 'not-connected' ? 'not-connected' : 'disconnected' }));
      }
    }
  }, [genba, trusstor, dispatch]);

  // Proactive Genba reconnect: Genba has no refresh, so its session dies at a
  // known time. Schedule ONE prompt for that moment instead of polling. (Trusstor
  // self-heals via refresh, so it gets no timer; everything else is reactive.)
  // `setTimeout` delays are clamped to a 32-bit int (~24.8 days) and overflow to
  // fire immediately, so we chunk long waits: arm for at most MAX_DELAY, then
  // re-check the real remaining time and re-arm — only dispatching once actually
  // expired. Re-arms when genbaExpiresAt changes.
  useEffect(() => {
    if (genba !== 'ok' || !genbaExpiresAt) return;
    const expiresAtMs = new Date(genbaExpiresAt).getTime();
    if (!Number.isFinite(expiresAtMs)) return;
    const MAX_DELAY = 2_147_483_647; // setTimeout's 32-bit ceiling
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      const remaining = expiresAtMs - Date.now();
      if (remaining <= 0) {
        dispatch(setProviderIssue({ provider: 'genba', issue: 'disconnected' }));
        return;
      }
      timer = setTimeout(arm, Math.min(remaining, MAX_DELAY));
    };
    arm();
    return () => clearTimeout(timer);
  }, [genba, genbaExpiresAt, dispatch]);

  // Only the roles that may open the portfolio get a way back to it.
  const canViewPortfolio = useCanAccess('portfolio.view');
  // The portfolio (home) spans every project, so it has no project or
  // schedule to show — its breadcrumb is the page name alone.
  const onPortfolio = useLocation().pathname === '/';

  return (
    <header className={styles.topbar}>
      {/* Left: portfolio button */}
      {canViewPortfolio ? (
        <>
          <button
            type="button"
            className={styles.portfolioBtn}
            onClick={() => navigate('/')}
            aria-label={t('topbar.backToPortfolio')}
          >
            <BriefcaseIcon size={15} />
            <span className={styles.portfolioLabel}>{t('topbar.portfolio')}</span>
          </button>

          <span className={styles.divider} aria-hidden="true" />
        </>
      ) : null}

      {/* Breadcrumb: project selector → chevron → (schedule picker → chevron) → page */}
      <nav className={styles.breadcrumb} aria-label={t('topbar.breadcrumb')}>
        {onPortfolio ? null : (
          <>
            <ProjectSelector />
            <ChevronRightIcon size={13} />
            <TopBarSchedulePickerSlot />
          </>
        )}
        <span className={styles.pageName}>{pageTitle}</span>
      </nav>

      {/* Right: reconnecting pill, notifications, user menu */}
      <div className={styles.actions}>
        {isReconnecting && (
          <span className={styles.reconnectingPill} role="status" aria-live="polite">
            <span className={styles.reconnectingDot} aria-hidden="true" />
            {t('monitor.streamStatus.reconnecting')}
          </span>
        )}

        <ProviderReconnectButton />

        <div className={styles.bellWrap}>
          <NotificationsBell />
        </div>

        <div className={styles.userMenuWrap}>
          <UserMenu showMeta={false} popupSide="bottom" />
        </div>
      </div>
    </header>
  );
}
